/**
 * GOT — Grow-Out Test, rebuilt as a replica of seedtrac's legacy QC-App (Android) and web GOT screens.
 * The only spec is docs/designs/got-legacy-map.md: every label, option, message and formula below is
 * taken from the legacy source or manual. Where the legacy code was broken, the INTENDED behaviour
 * is kept and the fix is named in a comment ("legacy bug"); nothing here is a NexSeed invention.
 * Anything the legacy evidence leaves open is marked UNKNOWN in the map and is not decided here.
 */

import { PRODUCTION_GRADES } from "./quality";

// ------------------------------------------------------------------ sample numbers

/** The Manual entry dialog: "AB" (2 letters) + "123456" (6 digits), concatenated (custompop_samplesearch). */
export function joinManualSampleNo(prefix: string, digits: string): string {
  return `${prefix.trim().toUpperCase()}${digits.trim()}`;
}
/**
 * BTS Seedling insists on exactly 8 characters ("Enter Valid Sample Number"): plant letter + year
 * letter + 6 digits (`DY003144`), which NexSeed mints since 2026-10-07. Samples minted before that
 * carry no year letter (`D003140`, 7 characters) and are still accepted.
 */
export const GOT_SAMPLE_NO_LENGTH = 8;
export const GOT_SAMPLE_NO_INVALID = "Enter Valid Sample Number";
export function isValidSampleNo(no: string): boolean {
  return /^[A-Z]{1,2}\d{6}$/i.test(no.trim());
}

// ------------------------------------------------------------------ option lists (exact legacy values)

/** Sample Receipt: "Acknowledgement Remark" spinner (SampleReceiptUpdateActivity:89). */
export const GOT_ACK_REMARKS = [
  "Received OK",
  "Damage",
  "Not Received",
] as const;
/** Sample Receipt: the two buttons, sent as `trtype`. */
export const GOT_RECEIPT_ACTIONS = ["Acknowledge", "Resampling"] as const;
export type GotReceiptAction = (typeof GOT_RECEIPT_ACTIONS)[number];

/** Planting: "Method of Sowing" (default Direct Sowing). */
export const GOT_SOWING_METHODS = ["Direct Sowing", "Nursery Sowing"] as const;
export type GotSowingMethod = (typeof GOT_SOWING_METHODS)[number];
/** Planting: "Nursery Medium", shown for Nursery Sowing only (default Soil Sowing). */
export const GOT_NURSERY_MEDIA = ["Soil Sowing", "Cocopeat Sowing"] as const;
export type GotNurseryMedium = (typeof GOT_NURSERY_MEDIA)[number];
/** Planting + Transplanting: "Direction" spinner, in the legacy order (default E-W). */
export const GOT_DIRECTIONS = [
  "E-W",
  "W-E",
  "E-W-E",
  "W-E-W",
  "N-S",
  "S-N",
  "N-S-N",
  "S-N-S",
] as const;

/** Field Monitoring Visit: "Crop Health" (default Good). Reasons are asked for Average / Poor only. */
export const GOT_CROP_HEALTH = ["Good", "Average", "Poor"] as const;
export type GotCropHealth = (typeof GOT_CROP_HEALTH)[number];
export const gotHealthNeedsReasons = (h: GotCropHealth) => h !== "Good";
/** Field Monitoring Visit: up to three optional photos ("Take Photo-1/2/3"). */
export const GOT_FMV_MAX_PHOTOS = 3;

/** Re-Test block on every data step: "With Sample" (default) / "Without Sample" + "Re-Test Reason". */
export const GOT_RETEST_TYPES = ["With Sample", "Without Sample"] as const;
export type GotRetestType = (typeof GOT_RETEST_TYPES)[number];
export const GOT_RETEST_REASON_MISSING = "Enter Re-Test Reason";

/** The legacy confirm dialog before every data submit (custom_confirmalert.xml). */
export const GOT_CONFIRM = {
  title: "Review Data Before Submission",
  message: "Are you sure you want to submit this data?",
  cancel: "No, Cancel",
  ok: "Yes, Submit it",
} as const;

/** Web review "GOT Status" select (user, 2026-10-05: all four); GRS print uses OK / RETEST / FAIL. */
export const GOT_RESULT_STATUSES = ["OK", "Fail", "BL", "RT"] as const;
export type GotResultStatus = (typeof GOT_RESULT_STATUSES)[number];
/** Lot GOT state as the legacy grids print it after the type: "GOT-R UT", "GOT-NR OK", ... */
export const GOT_LOT_STATES = ["UT", "OK", "Fail", "BL", "RT", "NUT"] as const;
export type GotLotState = (typeof GOT_LOT_STATES)[number];

/** Final GOT Grade (p221 / p246): the values seen in the legacy lists (user, 2026-10-05). */
export const GOT_BTS_GRADES = [
  "A+",
  "A",
  "B",
  "D",
  "Borderline",
  "Substandard",
] as const;
/** Same list as the arrival's Production Grade (user, 2026-10-15). */
export const GOT_PROD_GRADES = PRODUCTION_GRADES;
export const GOT_FINAL_GRADES = ["A", "B", "D", "BL", "Hold"] as const;

/** Gate Pass Out "Type" (p118). */
export const GOT_GATE_PASS_TYPES = ["Returnable", "Not Returnable"] as const;
/** Gate Pass Out "Particulars" row (p118). */
export const GOT_GATE_PASS_PARTICULARS = "GOT Samples";

/** GOT Dispatch "Select Dispatch Type" (p115). */
export const GOT_DISPATCH_TYPES = [
  "GOT Dispatch Pending",
  "GOT Dispatch Completed",
] as const;
/** The completed list only shows "last 7 days Records" (p117). */
export const GOT_DISPATCH_COMPLETED_DAYS = 7;

/** BTS Seedling: the (hidden in legacy, so always "BTS") decision radio. */
export const GOT_BTS_DECISIONS = ["BTS", "BTS and Field GOT (Both)"] as const;

/** The two parallel tests on the web GOT Result Update form (p255). */
export const GOT_TEST_KINDS = ["IN-SITU", "IN-TERRA"] as const;
/** Pending-report due windows printed under the report titles. */
export const GOT_REPORT_WINDOWS = {
  tppending: "Sowing Date + 30 Days",
  fmvpending: "Sowing Date + 70 Days",
  btspending: "Sowing Date + 70 Days",
} as const;

// ------------------------------------------------------------------ operator type + step gating

/** Login `oprtype`: Primary may only sow; Intermediate may do everything but Final Observation. */
export const GOT_OPERATOR_TYPES = ["Primary", "Intermediate"] as const;
export type GotOperatorType = (typeof GOT_OPERATOR_TYPES)[number];

export type GotStep =
  "planting" | "transplanting" | "monitoring" | "finalObservation";
export const GOT_STEP_LABELS: Record<GotStep, string> = {
  planting: "Planting",
  transplanting: "Transplanting",
  monitoring: "Field Monitoring",
  finalObservation: "Final Observation",
};

/** The server flags the steps hub reads (sampledatainfogot.php). */
export interface GotStepFlags {
  sowingDone: boolean;
  /** Transplanting is not applicable to a Direct Sowing (legacy transplantflg "2"). */
  transplantDone: boolean;
  transplantNotApplicable: boolean;
  /** At least one Field Monitoring Visit is recorded. */
  monitoringDone: boolean;
  finalObservationDone: boolean;
}

/**
 * Whether the operator type may open a step at all. Legacy bug: the Android hub only greyed the
 * Monitoring / Final Observation cards and left their buttons tappable; here the rule is enforced.
 */
export function operatorMayOpen(
  op: GotOperatorType | null,
  step: GotStep
): boolean {
  if (op === "Primary") return step === "planting";
  if (op === "Intermediate") return step !== "finalObservation";
  return true;
}

/**
 * A step card on the hub: `greyed` = done (or not applicable) and disabled, as legacy greys it;
 * `toast` = the legacy precondition toast shown on a tap when an earlier step is missing.
 * Legacy bugs fixed: Final Observation checked `sowingflg` while saying "Field Monitoring Data Not
 * Updated" (the message's rule is kept); Direct Sowing (transplantflg "2") could never reach
 * Monitoring. Monitoring stays open for repeat visits until Final Observation is done.
 * Field Monitoring is out of the flow (user, 2026-10-23): Final Observation waits for the
 * transplant (or a Direct Sowing), not for a visit.
 */
export function gotStepState(
  step: GotStep,
  f: GotStepFlags
): { greyed: boolean; toast: string | null } {
  switch (step) {
    case "planting":
      return { greyed: f.sowingDone, toast: null };
    case "transplanting":
      return {
        greyed: f.transplantDone || f.transplantNotApplicable,
        toast: f.sowingDone ? null : "Sowing Data Not Updated",
      };
    case "monitoring":
      return {
        greyed: f.finalObservationDone,
        toast:
          f.transplantDone || f.transplantNotApplicable
            ? null
            : "Transplanting Data Not Updated",
      };
    case "finalObservation":
      return {
        greyed: f.finalObservationDone,
        toast: !f.sowingDone
          ? "Sowing Data Not Updated"
          : f.transplantDone || f.transplantNotApplicable
            ? null
            : "Transplanting Data Not Updated",
      };
  }
}

// ------------------------------------------------------------------ arithmetic

/**
 * The legacy `#.##` DecimalFormat: at most 2 decimals, HALF_EVEN, no trailing zeros. Used for every
 * per-category % and Total % on Final Observation.
 */
export function legacy2dp(n: number): string {
  if (!Number.isFinite(n)) return "";
  const scaled = n * 100;
  const floor = Math.floor(scaled);
  const diff = scaled - floor;
  const EPS = 1e-9;
  let r: number;
  if (Math.abs(diff - 0.5) < EPS) r = floor % 2 === 0 ? floor : floor + 1;
  else r = Math.round(scaled);
  return String(r / 100);
}
/** `% = count × 100 / plants`; blank when plants is empty or 0 (legacy skips the calculation). */
export function gotPct(count: number | null, plants: number | null): string {
  if (count === null || plants === null || plants === 0) return "";
  return legacy2dp((count * 100) / plants);
}

// ------------------------------------------------------------------ Final Observation, per crop

export type GotObsCropSet = "generic" | "maize" | "paddy" | "bajra";

/** Routing is an exact (case-insensitive) crop-name match, as GOTResultStepsActivity does. */
export function gotObsCropSet(cropName: string): GotObsCropSet {
  const c = cropName.trim().toLowerCase();
  if (c === "maize seed") return "maize";
  if (c === "paddy seed") return "paddy";
  if (c === "bajra seed") return "bajra";
  return "generic";
}

export interface GotObsCategory {
  /** The legacy `fnobser_*` param stem: the count key; its % is `${key}Per`. */
  key: string;
  label: string;
  /** Paddy's in-form headings: "Same Stage", "Objectionable", "Early". */
  section?: string;
  /** The toast when the count is blank. */
  missing: string;
}

/**
 * Category sets in screen order, with the validation toasts in the legacy validation order. Female,
 * Male, OTF is always the order on screen, labelled F / M / OTF (user, 2026-10-23: "always shown as
 * F M OTF").
 */
export const GOT_OBS_CATEGORIES: Record<
  GotObsCropSet,
  readonly GotObsCategory[]
> = {
  generic: [
    {
      key: "femaleplants",
      label: "F",
      missing: "Enter No. of Female Plants",
    },
    {
      key: "maleplants",
      label: "M",
      missing: "Enter No. of Male Plants",
    },
    {
      key: "otherofftype",
      label: "OTF",
      missing: "Enter No. of Off Type Plants",
    },
  ],
  maize: [
    { key: "self", label: "Self Plants", missing: "Enter No. of Self Plant" },
    {
      key: "maleplants",
      label: "M",
      missing: "Enter No. of Male Plants",
    },
    {
      key: "otherofftype",
      label: "OTF",
      missing: "Enter No. of Off Type Plants",
    },
    {
      key: "pencilplants",
      label: "Pencil Plant",
      missing: "Enter No. of Pencil Plants",
    },
  ],
  paddy: [
    { key: "aline", label: "A Line", missing: "Enter No. of A Line Plants" },
    {
      key: "blinesh",
      label: "B Line/Shedder",
      missing: "Enter No. of B Line Plants",
    },
    {
      key: "lg",
      label: "Long Grain",
      section: "Same Stage",
      missing: "Enter No. of Off Long Grain",
    },
    {
      key: "fg",
      label: "Fine Grain",
      section: "Same Stage",
      missing: "Enter No. of Off Fine Grain",
    },
    {
      key: "bg",
      label: "Bold Grain",
      section: "Same Stage",
      missing: "Enter No. of Off Bold Grain",
    },
    {
      key: "rt",
      label: "Red Tip",
      section: "Same Stage",
      missing: "Enter No. of Off Red Tip",
    },
    {
      key: "tall",
      label: "Tall",
      section: "Objectionable",
      missing: "Enter No. of Off Tall",
    },
    {
      key: "late",
      label: "Late",
      section: "Objectionable",
      missing: "Enter No. of Off Late",
    },
    {
      key: "fertile",
      label: "Fertile",
      section: "Early",
      missing: "Enter No. of Off Fertile",
    },
    {
      key: "sterile",
      label: "Sterile",
      section: "Early",
      missing: "Enter No. of Off Sterile",
    },
    {
      key: "outcrosspart",
      label: "Out Cross/Partial fertile",
      section: "Early",
      missing: "Enter No. of Off Outcross",
    },
  ],
  // Legacy validates B line before Female although the screen shows Female first.
  bajra: [
    {
      key: "femaleplants",
      label: "F",
      missing: "Enter No. of Female Plants",
    },
    {
      key: "blineps",
      label: "B line/Pollen shedder",
      missing: "Enter No. of B Line",
    },
    {
      key: "otherofftype",
      label: "OTF",
      missing: "Enter No. of Off Type Plants",
    },
  ],
};
/** Bajra's validation order (B line, then Female, then Off-types). */
const BAJRA_CHECK_ORDER = ["blineps", "femaleplants", "otherofftype"];

/** The Total label per crop: "Total Plants" on the generic hybrid form, "Total" elsewhere. */
export const GOT_OBS_TOTAL_LABEL: Record<GotObsCropSet, string> = {
  generic: "Total Plants",
  maize: "Total",
  paddy: "Total",
  bajra: "Total",
};

export interface GotObsComputed {
  pct: Record<string, string>;
  /** Σ categories (the legacy Total includes every category, parent lines too). */
  total: number | null;
  totalPct: string;
}

/**
 * Per-category % and Total / Total % for a crop set. Legacy bugs fixed: Maize never calculated
 * (inverted conditions), Paddy Red Tip % overwrote Bold %, Paddy Out Cross % = 100 on a plants
 * change, Bajra Total % showed the off-type %, generic Total % unrounded.
 */
export function computeGotObservation(
  set: GotObsCropSet,
  plants: number | null,
  counts: Record<string, number | null>
): GotObsComputed {
  const cats = GOT_OBS_CATEGORIES[set];
  const pct: Record<string, string> = {};
  for (const c of cats) pct[c.key] = gotPct(counts[c.key] ?? null, plants);
  const all = cats.map((c) => counts[c.key] ?? null);
  const total = all.every((v) => v !== null)
    ? all.reduce<number>((a, v) => a + (v ?? 0), 0)
    : null;
  return { pct, total, totalPct: gotPct(total, plants) };
}

/**
 * GP % (genetic purity) = 100 − the Total off-type % (user 2026-10-07; legacy computed it on the
 * server and its formula is not in the evidence). Every Final Observation category is an off-type
 * (male, female/self, OOT, ...), so their Total % is the impurity. Blank when the Total % is.
 */
export function gotGeneticPurity(totalPct: string): string {
  if (totalPct === "") return "";
  return legacy2dp(100 - Number(totalPct));
}

/**
 * A resown sample's result (user, 2026-10-22: "added together, not averaged"): the replications'
 * No. of Plants and each category's count summed, then the %, Total and GP worked out on the sums.
 * Pass only the replications kept (a discarded one stays in history, out of the result).
 */
export function combineGotObservations(
  set: GotObsCropSet,
  rows: { plants: number | null; counts: Record<string, number | null> }[]
): GotObsComputed & {
  plants: number | null;
  counts: Record<string, number | null>;
  gp: string;
} {
  const sum = (xs: (number | null)[]) =>
    xs.every((v) => v !== null)
      ? xs.reduce<number>((a, v) => a + (v ?? 0), 0)
      : null;
  const plants = rows.length ? sum(rows.map((r) => r.plants)) : null;
  const counts: Record<string, number | null> = {};
  for (const c of GOT_OBS_CATEGORIES[set])
    counts[c.key] = rows.length
      ? sum(rows.map((r) => r.counts[c.key] ?? null))
      : null;
  const done = computeGotObservation(set, plants, counts);
  return { ...done, plants, counts, gp: gotGeneticPurity(done.totalPct) };
}

/** The legacy % param of a category's count (irregular: `maleplants` → `maleper`). */
export function gotPctStem(key: string): string {
  if (key === "maleplants") return "maleper";
  if (key === "femaleplants") return "femaleper";
  if (key === "otherofftype") return "otheroffper";
  return `${key}per`;
}

/**
 * Where each crop's Total goes: generic `fnobser_total`, Maize / Bajra `fnobser_totalofftype`.
 * Legacy bug fixed: Paddy calculated its Total and never sent it — it goes in `fnobser_total`.
 */
export function gotTotalStem(set: GotObsCropSet): "total" | "totalofftype" {
  return set === "maize" || set === "bajra" ? "totalofftype" : "total";
}

/** The first legacy toast that applies, or null. "Select Observatin Date" typo corrected. */
export function validateGotObservation(
  set: GotObsCropSet,
  v: {
    date: string;
    plants: number | null;
    counts: Record<string, number | null>;
  }
): string | null {
  if (!v.date) return "Select Observation Date";
  if (v.plants === null) return "Enter No. of Plants";
  const cats = GOT_OBS_CATEGORIES[set];
  const order =
    set === "bajra"
      ? BAJRA_CHECK_ORDER.map((k) => cats.find((c) => c.key === k)!)
      : cats;
  for (const c of order)
    if ((v.counts[c.key] ?? null) === null) return c.missing;
  return null;
}

// ------------------------------------------------------------------ Planting / Transplanting / FMV

export interface GotRetestInput {
  retest: boolean;
  retestType: GotRetestType;
  retestReason: string;
}

export interface GotPlantingInput extends GotRetestInput {
  method: GotSowingMethod;
  medium: GotNurseryMedium;
  date: string;
  state: string;
  location: string;
  direction: string;
  treyNumber: string;
  seeds: string;
  cellsPerTrey: string;
  treysPerLot: string;
  plotNo: string;
  rangeNo: string;
  bedNo: string;
  rows: string;
}

/** Which Planting rows are on screen (activity_g_o_t_planting visibility rules). */
export function gotPlantingFields(
  method: GotSowingMethod,
  medium: GotNurseryMedium
) {
  const cocopeat = method === "Nursery Sowing" && medium === "Cocopeat Sowing";
  return {
    medium: method === "Nursery Sowing",
    direction: !cocopeat,
    treyNumber: cocopeat,
    cocopeatCounts: cocopeat,
    plotRangeBedRows: !cocopeat,
    /** Cocopeat trays have no field row to start from (user, 2026-11-03). */
    startingPoint: !cocopeat,
  };
}

/**
 * Planting validation, in the legacy order. Legacy bug fixed: the Cocopeat checks compared the
 * Direct / Nursery radio with "Cocopeat Sowing" and never ran; they are applied (not for Paddy /
 * Bajra, as the legacy condition says). The always-hidden "Number of Plant" check is dropped, and
 * No. of Seeds is optional: the handheld no longer asks for it (user, 2026-10-26). No. of Rows is
 * optional on sowing too (user, 2026-10-27: nothing uses it); Transplanting still asks for it.
 */
export function validateGotPlanting(
  v: GotPlantingInput,
  cropName: string
): string | null {
  if (v.retest) return v.retestReason.trim() ? null : GOT_RETEST_REASON_MISSING;
  if (!v.location || v.location === "Select") return "Select Location";
  if (!v.state || v.state === "Select") return "Select State";
  if (!v.date) return "Select Sowing Date";
  if (v.method === "Direct Sowing") {
    if (!v.plotNo.trim()) return "Enter Plot No.";
    if (!v.rangeNo.trim()) return "Enter Range No.";
    if (!v.bedNo.trim()) return "Enter Bed No.";
  }
  const set = gotObsCropSet(cropName);
  if (
    gotPlantingFields(v.method, v.medium).cocopeatCounts &&
    set !== "paddy" &&
    set !== "bajra"
  ) {
    if (!v.cellsPerTrey.trim()) return "Enter No. of Cells";
    if (!v.treysPerLot.trim()) return "Enter No. of Treys";
  }
  return null;
}

export interface GotTransplantInput extends GotRetestInput {
  date: string;
  direction: string;
  state: string;
  location: string;
  plants: string;
  plot: string;
  range: string;
  bedNo: string;
  rows: string;
}

/** Transplanting validation, legacy order; Range is optional. */
export function validateGotTransplant(v: GotTransplantInput): string | null {
  if (v.retest) return v.retestReason.trim() ? null : GOT_RETEST_REASON_MISSING;
  if (!v.location || v.location === "Select") return "Select Location";
  if (!v.state || v.state === "Select") return "Select State";
  if (!v.date) return "Select Transplant Date";
  if (!v.plot.trim()) return "Enter Plot No.";
  if (!v.bedNo.trim()) return "Enter Bed No.";
  if (!v.rows.trim()) return "Enter No. of Rows";
  if (!v.plants.trim()) return "Enter No. of Plants";
  return null;
}

/**
 * Standard PPN's allowed shortfall (user, 2026-11-03, sheet "Seed Trac App (2).xlsx" → PPN: "10%
 * Deviation"): a count may fall 10% under the crop's Standard and still pass; only below the pass
 * mark does the replication wait for the supervisor (Low PPN). The SQL twin is `ppnBelow` in the
 * API's got module.
 */
export const GOT_PPN_DEVIATION = 0.1;
/** The fewest plants that pass: Standard 150 → 135, 200 → 180. */
export const gotPpnPassMark = (standard: number): number =>
  Math.ceil(standard * (1 - GOT_PPN_DEVIATION) - 1e-9);
/** A count below its Standard's pass mark; no Standard = no check. */
export const gotPpnBelow = (ppn: number, standard: number | null): boolean =>
  standard !== null && ppn < gotPpnPassMark(standard);

/**
 * The Expected Date of Observation (user, 2026-11-03; the EDOR sheet: "From Sowing Date to
 * Observation Date"): the Date of Sowing plus the crop's days. yyyy-mm-dd in, yyyy-mm-dd out, in
 * UTC so no time zone shifts it. Null when either is missing or the date is unreadable.
 */
export function gotExpectedObservationDate(
  dosow: string | null | undefined,
  days: number | null | undefined
): string | null {
  if (!dosow || days == null || !Number.isFinite(days)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dosow);
  if (!m) return null;
  const t = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]) + Math.trunc(days)
  );
  return Number.isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
}

/**
 * Where a sample's plants stand (the transplant, else a Direct Sowing) or where it was raised (the
 * Soil nursery bed, or the Cocopeat tray), as the field team entered it (user, 2026-10-27: the bed
 * numbers only; the team works out where each sample starts and ends).
 */
export interface GotFieldSpot {
  kind: "field" | "nursery" | "tray";
  location: string | null;
  plot: string | null;
  range: string | null;
  bedNo: string | null;
  rows: string | null;
  direction: string | null;
  trayNo: string | null;
}

/** "Plot 4 · Range 2 · Bed 1-3" (or "Tray 7"), the location first when asked. */
export function gotSpotText(s: GotFieldSpot, withLocation = false): string {
  const parts =
    s.kind === "tray"
      ? [s.trayNo ? `Tray ${s.trayNo}` : null]
      : [
          s.plot ? `Plot ${s.plot}` : null,
          s.range ? `Range ${s.range}` : null,
          s.bedNo ? `Bed ${s.bedNo}` : null,
        ];
  return [withLocation ? s.location : null, ...parts]
    .filter(Boolean)
    .join(" · ");
}

/** FMV: only the Re-Test reason is ever required. Reasons are dropped when health is Good (legacy bug: stale reasons were sent). */
export function validateGotMonitoring(v: GotRetestInput): string | null {
  return v.retest && !v.retestReason.trim() ? GOT_RETEST_REASON_MISSING : null;
}

// ------------------------------------------------------------------ dates

/** Legacy screens show DD-MM-YYYY (zero-padded; legacy bug: picked dates lost their padding). */
export function gotDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}-${m}-${y}` : "";
}

// ------------------------------------------------------------------ web GOT Result Update (p255–258)

/** IN-TERRA Observations on the web (p258 columns): each % = No. × 100 / Plant Population. */
export const GOT_WEB_OBS_CATEGORIES = [
  { key: "femaleBranching", label: "F/Branching" },
  { key: "maleDesi", label: "M/Desi Type" },
  { key: "oof", label: "OTF" },
] as const;

/** IN-SITU GEA counts (p258): each % = No. × 100 / Amplified Samples. UNKNOWN: legacy denominator — confirm. */
export const GOT_GEA_CATEGORIES = [
  { key: "female", label: "F" },
  { key: "male", label: "M" },
  { key: "outcross", label: "Outcross" },
  { key: "oof", label: "OTF" },
] as const;

/** Replication counts offered by the web form's "No. of Replications" selects. UNKNOWN beyond 1 (only "1" is seen). */
export const GOT_REPLICATION_OPTIONS = [1, 2, 3, 4] as const;

// ------------------------------------------------------------------ handheld lists (QC-App)

/**
 * Sample Allocation: add a scanned sample unless it is already listed. Legacy bug fixed: the
 * Android loop added while iterating and crashed on the second different sample.
 */
export function gotAllocAdd<T extends { sampleNo: string }>(
  list: readonly T[],
  sample: T
): T[] {
  return list.some((r) => r.sampleNo === sample.sampleNo)
    ? [...list]
    : [...list, sample];
}

/** Review and Final Submit: tick / untick a sample number; never listed twice (legacy bug 15). */
export function gotToggleSample(
  ticked: readonly string[],
  sampleNo: string
): string[] {
  return ticked.includes(sampleNo)
    ? ticked.filter((s) => s !== sampleNo)
    : [...ticked, sampleNo];
}

/**
 * Field Monitoring Visit: the reasons sent. Good sends none; Average / Poor send only the ticked
 * reasons the current health offers. Legacy bug fixed: reasons ticked under an earlier health
 * choice were still submitted.
 */
export function gotFmvReasonsToSend(
  health: GotCropHealth,
  ticked: readonly string[],
  offered: readonly string[]
): string[] {
  if (!gotHealthNeedsReasons(health)) return [];
  return offered.filter((r) => ticked.includes(r));
}

/** The four QC-App report keys (`reptype`), in ReportHomeActivity's button order. */
export const GOT_APP_REPORT_KEYS = [
  "sowpending",
  "tppending",
  "fmvpending",
  "btspending",
] as const;
export type GotAppReportKey = (typeof GOT_APP_REPORT_KEYS)[number];

export interface GotReportColumn {
  label: string;
  /** The `gotrepdetailsarray` JSON key, camelCased. */
  field: string;
  date?: boolean;
}

/**
 * QC-App reports: the home button, the summary title, the detail screen heading, and the detail
 * columns (Appendix B §7). Legacy bug fixed: the Transplanting report's last three columns were
 * wired to the wrong keys (Bed No. showed sownooftray, Trey No. tpbedno, No. of Trey sowtrayno).
 * Crop, Variety, Lot No., Sample No. sit side by side, SP Code after them (user, 2026-10-23).
 */
export const GOT_APP_REPORTS: Record<
  GotAppReportKey,
  {
    button: string;
    title: string;
    heading: string;
    columns: readonly GotReportColumn[];
  }
> = {
  sowpending: {
    button: "Sowing Pending Report",
    title: "Sowing Pending Report",
    heading: "Sowing Pending",
    columns: [
      { label: "Date Of Arrival", field: "arrivalDate", date: true },
      { label: "Crop", field: "crop" },
      { label: "Variety", field: "variety" },
      { label: "Lot No", field: "lotNo" },
      { label: "Sample", field: "sampleNo" },
      { label: "SP Code", field: "spCodes" },
      { label: "NoB", field: "nob" },
      { label: "Qty", field: "qty" },
      { label: "GOT Status", field: "gotStatus" },
      { label: "PD Loc", field: "prodLoc" },
      { label: "Organiser", field: "organizer" },
      { label: "Farmer", field: "farmer" },
      { label: "PP", field: "prodPer" },
      { label: "SLOC", field: "sloc" },
    ],
  },
  tppending: {
    button: "Transplanting Pending Report",
    title: "Transplanting Pending Report",
    heading: "Transplanting Pending",
    columns: [
      { label: "DOA", field: "arrivalDate", date: true },
      { label: "Crop", field: "crop" },
      { label: "Variety", field: "variety" },
      { label: "Lot No.", field: "lotNo" },
      { label: "Sample No.", field: "sampleNo" },
      { label: "SP Code", field: "spCodes" },
      { label: "DOS", field: "dateOfSowing", date: true },
      { label: "NL", field: "sowNurseryLoc" },
      { label: "Bed No.", field: "sowBedNo" },
      { label: "Trey No.", field: "sowTrayNo" },
      { label: "No. of Trey", field: "sowNoOfTray" },
    ],
  },
  fmvpending: {
    button: "GOT Observation Pending Report",
    title: "GOT Observation Pending Report",
    heading: "GOT Observation Pending",
    columns: [
      { label: "Crop", field: "crop" },
      { label: "Variety", field: "variety" },
      { label: "Lot No.", field: "lotNo" },
      { label: "Sample No.", field: "sampleNo" },
      { label: "SP Code", field: "spCodes" },
      { label: "TL", field: "sowNurseryLoc" },
      { label: "DOT", field: "dateOfTp", date: true },
      { label: "Bed No", field: "sowBedNo" },
      { label: "Direction", field: "tpDirection" },
    ],
  },
  btspending: {
    button: "Biotech Test Initiative Report",
    title: "Biotech Test Initiative Report",
    heading: "Bio Tech Test Initiative Pending",
    columns: [
      { label: "Crop", field: "crop" },
      { label: "Variety", field: "variety" },
      { label: "Lot No.", field: "lotNo" },
      { label: "Sample No.", field: "sampleNo" },
      { label: "SP Code", field: "spCodes" },
      { label: "Arrival Date", field: "arrivalDate", date: true },
      { label: "DOS", field: "dateOfSowing", date: true },
      { label: "TRD", field: "ttivResultDate", date: true },
    ],
  },
};
/** The detail screen's window line, where legacy prints one. */
export function gotReportWindow(key: GotAppReportKey): string | null {
  return key === "sowpending" ? null : GOT_REPORT_WINDOWS[key];
}

/** BTS Seedling / Dispatch form instructions (strings.xml bts_seedling_submit / _back). */
export const GOT_BTS_TEXT = {
  seedlingSubmit:
    "SUBMIT: Sample will be posted to BTS Seedling In-Progress List",
  dispatchSubmit:
    "SUBMIT: Sample will be posted to BTS Seedling Dispatch In-Progress List",
  back: "BACK : Cancels the scanned sample and takes back for fresh scan",
} as const;

/** The replication radio on the steps hub, sent as the legacy `repString` values. */
export const GOT_REPLICATIONS = [
  { label: "R1", value: "ReplicationOne" },
  { label: "R2", value: "ReplicationTwo" },
] as const;
export type GotReplication = (typeof GOT_REPLICATIONS)[number]["value"];
